import { TacticTooltip } from '@/components/basic';
import { AddAssignmentModal } from '@/components/resources/modals/AddAssignmentModal/AddAssignmentModal';
import { accessAssignmentGroups } from '@/components/resources/tabs/access-restrictions/defines';
import { IResource } from '@gettactic/api';
import { AssignmentGroupType } from '@gettactic/api';
import { Box } from '@gettactic/components/src/primitives/Box';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@gettactic/components/src/primitives/DropdownMenu';
import { Flex } from '@gettactic/components/src/primitives/Flex';
import { useDisclosure } from '@gettactic/helpers';
import { getObjectKeys } from '@gettactic/helpers/src/objects/getObjectKeys';
import React from 'react';
import { IoAdd as Plus } from 'react-icons/io5';
import { Button } from '@gettactic/components';
import { useIsDesignSystem2 } from '@gettactic/hooks';
export interface CreateAssignerDisclosureOptions {
  group: AssignmentGroupType;
}

export interface AddAssignmentButtonProps {
  resources: IResource[];
  hasAdditionalAccess: boolean;
  isAllUsersEnabled: boolean;
  onRefresh: () => void;
}

export const AddAssignmentButton: React.FC<AddAssignmentButtonProps> = ({
  resources,
  hasAdditionalAccess,
  isAllUsersEnabled,
  onRefresh
}) => {
  const addAssignmentsDisclosure =
    useDisclosure<CreateAssignerDisclosureOptions>();

  const closeAddAssignmentsModal = () => {
    onRefresh();
    addAssignmentsDisclosure.close();
  };

  const titleDisabled = !hasAdditionalAccess
    ? 'You need to have enabled Additional Access to add a Secondary Group'
    : isAllUsersEnabled
      ? "You can't add a Secondary Group because you already enabled All Users"
      : null;

  const isV2 = useIsDesignSystem2();

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger>
          <Button
            variant={isV2 ? 'primary' : 'blue'}
            size="base"
            layout="squared"
            className="ml-4 inline-flex"
            iconLeft={Plus}
          >
            Add Assignment
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>
            <Flex className="items-center">
              Assignment Groups{' '}
              {titleDisabled ? (
                <TacticTooltip>{titleDisabled}</TacticTooltip>
              ) : null}
            </Flex>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {getObjectKeys(accessAssignmentGroups).map((groupId) => {
            const group = accessAssignmentGroups[groupId];
            if (!group) return null;
            const isDisabled =
              group.isAdditional && (!hasAdditionalAccess || isAllUsersEnabled);

            return (
              <DropdownMenuItem
                disabled={isDisabled}
                key={groupId}
                onClick={addAssignmentsDisclosure.open.bind(null, {
                  group: groupId
                })}
              >
                <Flex className={'items-center gap-2'}>
                  <Box
                    className={'size-3 rounded-full'}
                    style={{ backgroundColor: group.color }}
                  />
                  {group.label}
                </Flex>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      <AddAssignmentModal
        resources={resources}
        onCreated={closeAddAssignmentsModal}
        onClose={closeAddAssignmentsModal}
        opened={addAssignmentsDisclosure.isOpen}
        group={addAssignmentsDisclosure.data?.group ?? ('Primary' as const)}
      />
    </>
  );
};
